import { useNavigate } from "react-router-dom";
import { buyerPosts } from "@/mocks/products";

const previewPosts = buyerPosts.slice(0, 6);

export default function BuyerQuoteSection() {
  const navigate = useNavigate();

  return (
    <section className="py-16 bg-stone-50">
      <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
          <div>
            <p className="text-amber-500 text-sm font-semibold uppercase tracking-widest mb-1">Live Sourcing Requests</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-stone-900 leading-tight">
              Buyer Requirements<br className="hidden md:block" /> &amp; Quote Board
            </h2>
            <p className="text-stone-500 text-sm mt-2 max-w-xl">
              28 active buyer requests from verified global buyers. Posting is always <strong className="text-green-600">free</strong> for buyers — factories need a membership to view and send quotes.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <button
              onClick={() => navigate("/buyer-post")}
              className="flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-bold px-5 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-add-line"></i> Post Free Requirement
            </button>
            <button
              onClick={() => navigate("/buyer-post")}
              className="flex items-center justify-center gap-2 bg-white border border-stone-200 text-stone-700 font-bold px-5 py-2.5 rounded-full hover:border-amber-300 transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              View All 28 Requests <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>

        {/* Info pills */}
        <div className="flex flex-wrap gap-3 mb-8">
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-xs font-bold px-4 py-2 rounded-full">
            <i className="ri-checkbox-circle-fill text-green-500"></i>
            Buyer Post — Always FREE
          </div>
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-bold px-4 py-2 rounded-full">
            <i className="ri-vip-crown-line text-amber-500"></i>
            Factory View &amp; Quote — Membership Required
          </div>
          <div className="flex items-center gap-2 bg-stone-100 text-stone-600 text-xs font-bold px-4 py-2 rounded-full">
            <i className="ri-global-line"></i>
            22+ Countries · $2.8M+ Total Budget
          </div>
        </div>

        {/* Preview cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {previewPosts.map((post) => (
            <div
              key={post.id}
              onClick={() => navigate("/buyer-post")}
              className="bg-white rounded-2xl p-5 border border-stone-100 hover:border-amber-200 cursor-pointer group transition-all hover:-translate-y-0.5"
            >
              <div className="flex items-start justify-between mb-3 gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
                    <span className="text-xs bg-amber-50 text-amber-600 font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">{post.category}</span>
                    {post.verified && <span className="text-xs text-green-600 font-semibold bg-green-50 px-2 py-0.5 rounded-full whitespace-nowrap flex items-center gap-0.5"><i className="ri-shield-check-fill text-xs"></i> Verified</span>}
                    {post.urgent && <span className="text-xs text-red-600 font-semibold bg-red-50 px-2 py-0.5 rounded-full whitespace-nowrap flex items-center gap-0.5"><i className="ri-alarm-line text-xs"></i> Urgent</span>}
                  </div>
                  <h3 className="font-bold text-stone-800 text-sm leading-snug line-clamp-2">{post.title}</h3>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="bg-stone-50 rounded-lg px-2.5 py-1.5">
                  <div className="text-xs text-stone-400">Budget</div>
                  <div className="text-xs font-bold text-stone-800 truncate">{post.budget}</div>
                </div>
                <div className="bg-stone-50 rounded-lg px-2.5 py-1.5">
                  <div className="text-xs text-stone-400">Quantity</div>
                  <div className="text-xs font-bold text-stone-800 truncate">{post.quantity}</div>
                </div>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-stone-100">
                <div className="flex items-center gap-1.5 text-xs text-stone-500">
                  <span>{post.flag}</span>
                  <span className="truncate">{post.buyer}</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-stone-400">
                  <i className="ri-chat-3-line text-amber-400"></i>
                  <span>{post.responses} quotes</span>
                </div>
              </div>
              {/* Blurred contact */}
              <div className="relative mt-3 rounded-lg overflow-hidden">
                <div className="bg-stone-50 px-2.5 py-1.5 blur-sm select-none pointer-events-none text-xs text-stone-500">
                  📧 buyer@company.com · 📞 +1 555-000-0000
                </div>
                <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                  <span className="text-xs font-bold text-amber-600 flex items-center gap-1">
                    <i className="ri-lock-line"></i> Membership required
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="bg-stone-900 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="bg-amber-400 text-white text-xs font-bold px-3 py-1 rounded-full">28 Active Requests</span>
              <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">Buyers Post Free</span>
            </div>
            <h3 className="text-white font-extrabold text-xl mb-1">Factory? Unlock All 28 Buyer Requests</h3>
            <p className="text-stone-400 text-sm">Get a membership to view full buyer details, contact info, and send unlimited quotes.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <button
              onClick={() => navigate("/buyer-post")}
              className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i> Post Free Requirement
            </button>
            <button
              onClick={() => navigate("/membership")}
              className="flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-extrabold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-vip-crown-line"></i> Get Membership
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
